import {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Bar, Line} from 'react-chartjs-2';
import {
  averageNumber,
  baseOptions,
  fillPeriods,
  plainNumber,
  seriesColor,
  seriesWash,
} from './chartTheme';
import {ChartFrame, EmptyState, StatCard, StatRow} from './DashboardUI';
import styles from './dashboard.module.scss';

/**
 * Signups aggregated by week, month or year, over the whole history.
 *
 * Weekly and monthly are lines. Both run over hundreds of consecutive periods —
 * 216 weeks of record — and at that density bars collapse into hairlines: a
 * single spike week reads as one stray vertical rule on an empty field, and the
 * shape of everything around it disappears. A line keeps the trajectory legible
 * across the whole span, which is the only reason to look at this range.
 *
 * Yearly stays bars. Five discrete years are five separate quantities to
 * compare, not a path through time, and there are few enough of them that each
 * bar carries real width.
 *
 * `fillPeriods` matters as much here as the daily filler does — 44 monthly
 * points cover roughly fifty months, so a quiet month was being skipped rather
 * than drawn at zero.
 */

const UNIT_NOUN = {week: 'week', month: 'month', year: 'year'};

/**
 * How much horizontal room one period needs to stay readable. Below roughly
 * this, weekly labels collide and the line becomes a single dense smear.
 */
const WIDTH_PER_PERIOD = {week: 16, month: 34, year: 0};

/** Width of the pinned value axis; mirrored by `.pinnedAxis` in the stylesheet. */
const AXIS_WIDTH = 64;

export const TrendChart = ({points, unit, loading}) => {
  const series = useMemo(() => fillPeriods(points, unit), [points, unit]);
  const asBars = unit === 'year';
  const lastIndex = series.length - 1;
  const scrollerRef = useRef(null);

  const [scrollable, setScrollable] = useState(false);

  /**
   * An explicit ceiling, rounded up to something a person would pick.
   *
   * The scrolling plot and the pinned axis are two separate canvases, so they
   * have to be told the same maximum — left to their own devices Chart.js would
   * choose independently and the labels would stop describing the line.
   */
  const yMax = useMemo(() => {
    const peak = series.reduce(
      (best, point) => Math.max(best, point.amount),
      0,
    );
    if (peak <= 4) return 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(peak)));
    return Math.ceil(peak / magnitude) * magnitude;
  }, [series]);

  // A floor rather than a width, so a short series still fills the card and a
  // long one overflows into a scroll. Five years always fit; two hundred weeks
  // never do.
  const minContentWidth = useMemo(() => {
    const per = WIDTH_PER_PERIOD[unit] ?? 0;
    return per && series.length ? series.length * per : null;
  }, [series.length, unit]);

  // Park the view on the most recent period. A time series is read from now
  // backwards, and opening on 2022 would hide everything that matters.
  //
  // Re-run on resize as well: a rotation or a pane change alters how many
  // periods fit, which decides both where "most recent" sits and whether the
  // scroll hint is true at all.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) {
      setScrollable(false);
      return undefined;
    }

    const settle = () => {
      el.scrollLeft = el.scrollWidth;
      setScrollable(el.scrollWidth > el.clientWidth + 1);
    };

    settle();
    window.addEventListener('resize', settle);
    return () => window.removeEventListener('resize', settle);
  }, [minContentWidth, series.length, unit]);

  const summary = useMemo(() => {
    if (!series.length) return null;
    const values = series.map(point => point.amount);
    const total = values.reduce((sum, value) => sum + value, 0);
    const peakIndex = values.reduce(
      (best, value, index) => (value > values[best] ? index : best),
      0,
    );
    // The latest period is usually still running, so it is not a fair
    // comparison against the one before it — it is called out, not hidden.
    return {
      total,
      peak: series[peakIndex],
      latest: series[series.length - 1],
      average: total / series.length,
      periods: series.length,
    };
  }, [series]);

  const chartData = useMemo(() => {
    if (asBars)
      return {
        labels: series.map(point => point.label),
        datasets: [
          {
            label: 'Users',
            data: series.map(point => point.amount),
            backgroundColor: series.map((point, index) =>
              index === lastIndex ? seriesColor(3) : seriesColor(0),
            ),
            borderRadius: 4,
            maxBarThickness: 34,
          },
        ],
      };

    return {
      labels: series.map(point => point.label),
      datasets: [
        {
          label: 'Users',
          data: series.map(point => point.amount),
          borderColor: seriesColor(0),
          backgroundColor: seriesWash(0),
          borderWidth: 2,
          // Over two hundred periods, a dot per point is a solid band. Only the
          // in-progress period is marked, and the rest appear on hover.
          pointRadius: context => (context.dataIndex === lastIndex ? 4 : 0),
          pointBackgroundColor: '#ffffff',
          pointBorderColor: seriesColor(3),
          pointBorderWidth: 2,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: '#ffffff',
          pointHoverBorderColor: seriesColor(0),
          pointHoverBorderWidth: 2,
          fill: true,
          // Straight segments: a spline through period counts invents values
          // between them and can swing below zero on the way.
          tension: 0,
        },
      ],
    };
  }, [series, asBars, lastIndex]);

  const options = useMemo(() => {
    const base = baseOptions({integer: true, valueLabel: 'Users'});
    return {
      ...base,
      layout: {padding: {right: 8}},
      scales: {
        ...base.scales,
        x: {
          ...base.scales.x,
          ticks: {
            ...base.scales.x.ticks,
            maxTicksLimit: unit === 'year' ? 12 : 14,
          },
        },
        y: {
          ...base.scales.y,
          max: yMax,
          // When the axis is pinned alongside, this canvas keeps the gridlines
          // but hands the numbers to the fixed one.
          ticks: {...base.scales.y.ticks, display: !scrollable},
        },
      },
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            title: items => items[0].label,
            label: context =>
              ` Users: ${plainNumber(context.parsed.y)}${
                context.dataIndex === lastIndex
                  ? ` (${UNIT_NOUN[unit]} still in progress)`
                  : ''
              }`,
          },
        },
      },
    };
  }, [lastIndex, unit, scrollable, yMax]);

  const axisData = useMemo(
    () => ({
      labels: [''],
      datasets: [{data: [0], borderWidth: 0, pointRadius: 0}],
    }),
    [],
  );

  const axisOptions = useMemo(() => {
    const base = baseOptions({integer: true});
    return {
      ...base,
      animation: false,
      events: [],
      plugins: {legend: {display: false}, tooltip: {enabled: false}},
      layout: {padding: {top: 0, bottom: 0, left: 0, right: 0}},
      scales: {
        /**
         * The x axis is kept and blanked rather than hidden. Hiding it gives
         * this canvas a taller plot area than the scrolling one, and the two
         * sets of gridlines then sit at different heights — the labels stop
         * describing the line they are supposed to measure. Rendering a space
         * per tick reserves exactly the same strip.
         */
        x: {
          ...base.scales.x,
          grid: {display: false},
          border: {display: false},
          ticks: {...base.scales.x.ticks, callback: () => ' '},
        },
        y: {
          ...base.scales.y,
          max: yMax,
          grid: {display: false},
          /**
           * Chart.js sizes an axis from what it thinks the labels need, and
           * with no grid and no border to anchor it, it under-reserved and drew
           * "5,000" half outside the canvas. Pinning the width leaves the
           * labels a strip they are guaranteed to fit in.
           */
          afterFit: scale => {
            scale.width = AXIS_WIDTH - 4;
          },
        },
      },
    };
  }, [yMax]);

  if (!loading && !series.length)
    return (
      <EmptyState
        title="No signup history yet"
        description="Once accounts start being created the trend appears here."
      />
    );

  return (
    <div className={styles.section}>
      <StatRow columns={4}>
        <StatCard
          label="Total signups"
          value={plainNumber(summary?.total)}
          hint={`across ${plainNumber(summary?.periods)} ${
            UNIT_NOUN[unit]
          }s on record`}
          loading={loading}
        />
        <StatCard
          label={`Best ${UNIT_NOUN[unit]}`}
          value={plainNumber(summary?.peak?.amount)}
          hint={summary?.peak?.label}
          loading={loading}
        />
        <StatCard
          label={`Per ${UNIT_NOUN[unit]}`}
          value={summary ? averageNumber(summary.average) : '—'}
          hint="average across the record"
          loading={loading}
        />
        <StatCard
          label={`Latest ${UNIT_NOUN[unit]}`}
          value={plainNumber(summary?.latest?.amount)}
          hint={
            summary?.latest
              ? `${summary.latest.label} — still in progress`
              : undefined
          }
          loading={loading}
        />
      </StatRow>

      <div className={styles.card}>
        <div className={styles.chartRow}>
          {/* The axis lives outside the scroller so the numbers stay put while
              the periods move. Inside it, the labels scrolled off the left and
              the chart became unreadable the moment you moved it. */}
          {scrollable && (
            <div className={styles.pinnedAxis} aria-hidden="true">
              <Line data={axisData} options={axisOptions} />
            </div>
          )}
          <ChartFrame
            height={320}
            minContentWidth={minContentWidth}
            scrollRef={scrollerRef}>
            {asBars ? (
              <Bar data={chartData} options={options} />
            ) : (
              <Line data={chartData} options={options} />
            )}
          </ChartFrame>
        </div>
        {scrollable && (
          <p className={styles.scrollHint}>
            Showing the most recent {UNIT_NOUN[unit]}s — scroll left for older.
          </p>
        )}
      </div>
    </div>
  );
};

export default TrendChart;
